import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, BadgeCheck, Users, Trophy, Crosshair } from "lucide-react";

export default function TeamRoster() {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setTeam(null); setPlayers([]); setNotFound(false);
    Promise.all([
      api.get(`/teams/${teamId}`),
      api.get(`/players?team_id=${encodeURIComponent(teamId)}`),
    ]).then(([t, p]) => {
      setTeam(t.data);
      setPlayers(p.data || []);
    }).catch((e) => setNotFound(e?.response?.status === 404));
  }, [teamId]);

  const roster = useMemo(() => players.filter((p) => p.team_id === teamId), [players, teamId]);
  const sorted = useMemo(
    () => [...roster].sort((a, b) => (b.kills || 0) - (a.kills || 0)),
    [roster],
  );

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="team-not-found">
        <div className="text-[10px] font-mono uppercase tracking-widest text-red-500 mb-2">/ TEAM NOT FOUND</div>
        <Link to="/teams" className="text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-4 py-2 uppercase text-xs font-mono tracking-widest inline-flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Back to teams
        </Link>
      </div>
    );
  }

  if (!team) return <div className="max-w-5xl mx-auto p-8" data-testid="team-roster-loading"><div className="font-mono text-gray-500 text-xs">Loading roster…</div></div>;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="team-roster-page">
      <Link to="/teams" className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-cyan-400 inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3 h-3" /> Teams
      </Link>
      <div className="flex items-start gap-4 mb-8">
        <div
          className="w-16 h-16 border-2 flex items-center justify-center font-black text-xl shrink-0"
          style={{ borderColor: team.logo_color || "#8b93f9", color: team.logo_color || "#8b93f9" }}
        >
          {team.tag}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">/ ROSTER</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight uppercase truncate">
            {team.name}
          </h1>
          <div className="flex flex-wrap gap-3 mt-2 font-mono text-xs text-gray-500">
            <span>{team.division || "Unassigned"}</span>
            <span>·</span>
            <span>{roster.length} player{roster.length === 1 ? "" : "s"}</span>
            {team.manager && <><span>·</span><span>Mgr: <span className="text-gray-300">{team.manager}</span></span></>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-6">
        {[
          { l: "W", v: team.wins || 0 },
          { l: "D", v: team.draws || 0 },
          { l: "L", v: team.losses || 0 },
          { l: "PTS", v: team.points || 0 },
        ].map((s) => (
          <div key={s.l} className="border border-[#232330] p-3">
            <div className="text-[9px] text-gray-500 font-mono tracking-widest">{s.l}</div>
            <div className="font-mono font-bold text-base text-white">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="border border-[#232330]">
        <div className="px-4 py-3 border-b border-[#232330] bg-[#0a0a12] flex items-center gap-2">
          <Users className="w-4 h-4 text-cyan-400" />
          <span className="font-heading font-black uppercase tracking-tight">Full roster</span>
        </div>
        {sorted.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-500">No players rostered yet.</div>
        ) : (
          <ul className="divide-y divide-[#232330]">
            {sorted.map((p) => {
              const kd = ((p.kills || 0) / Math.max(1, p.deaths || 0)).toFixed(2);
              return (
                <li key={p.id} data-testid={`roster-row-${p.handle}`} className="flex items-center gap-3 px-4 py-3 hover:bg-[#0a0a12]">
                  <Link
                    to={`/player-id/${encodeURIComponent(p.id)}`}
                    className="flex-1 min-w-0 flex items-center gap-2 hover:text-cyan-400 transition-colors"
                  >
                    <span className="font-heading font-bold uppercase tracking-tight truncate">{p.handle}</span>
                    {p.hll_verified && <BadgeCheck className="w-3.5 h-3.5 text-cyan-400 shrink-0" />}
                    {p.role && <span className="text-[9px] font-mono uppercase tracking-widest text-gray-500">· {p.role}</span>}
                  </Link>
                  <div className="hidden sm:flex items-center gap-4 font-mono text-[11px] text-gray-500">
                    <span><Crosshair className="w-3 h-3 inline-block mr-1 text-[#F4D03F]" />{p.kills || 0}</span>
                    <span>{p.matches || 0} mp</span>
                    <span className="text-cyan-400 font-bold">{kd} K/D</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
